import { useAuth } from 'context/AuthContext';
import Cookie from 'js-cookie';
import { useEffect, useState } from 'react';

/** How long auth may sit unresolved before we stop calling it "loading". */
export const AUTH_STALL_MS = 15000;

export type DashboardAuthState = {
  /** Auth is still resolving and has not been doing so for too long. */
  isResolving: boolean;
  /**
   * Auth has been unresolved past AUTH_STALL_MS. The user-document listener
   * fires no callback at all when Firestore is unreachable, so this state is
   * reachable and does not clear itself — the page must offer a way out
   * rather than showing a skeleton forever.
   */
  isStalled: boolean;
  /** Auth resolved to nobody. We cannot state anything about their account. */
  isSignedOut: boolean;
  /** True once we have a user document to query with. */
  isReady: boolean;
  /** Re-fetches the user document; the action behind the stalled retry. */
  retry: () => void;
};

/**
 * Collapses AuthContext's two-step resolution (Firebase user, then Firestore
 * user document) into the states a dashboard page has to render differently.
 *
 * The distinction that matters: `user === undefined` means "we do not know
 * yet", `user === null` means "resolved, nobody is signed in". Treating the
 * second as the first leaves a permanent spinner; treating it as a normal
 * signed-in user is what made these pages report "$0.00" and "No Purchases"
 * to people we had not identified.
 */
export function useDashboardAuthState(): DashboardAuthState {
  const { user, userDoc, isLoadingUserDoc, refreshUserDoc } = useAuth();

  // A null user while the auth cookie is still live is not a logged-out
  // visitor. AuthContext deliberately keeps that cookie through transient
  // nulls (iOS Safari tab resume, GB #1435) and only removes it on an
  // explicit logout, and the middleware reads the same cookie to decide
  // access. Calling this "signed out" would swap a signed-in seller's
  // dashboard for a sign-in wall on a tab resume.
  const sessionMayStillBeValid = user === null && !!Cookie.get('auth');

  const unresolved =
    user === undefined || isLoadingUserDoc || sessionMayStillBeValid;
  const [isStalled, setIsStalled] = useState(false);
  // Re-arms the timer after a retry. Without it a retry that also fails would
  // leave the page in the loading state with no second chance to time out.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!unresolved) {
      setIsStalled(false);
      return;
    }
    const timer = setTimeout(() => setIsStalled(true), AUTH_STALL_MS);
    return () => clearTimeout(timer);
  }, [unresolved, attempt]);

  // The cookie buys a grace period, not an indefinite one. If the wait times
  // out while Firebase is telling us there is no user, the cookie is stale or
  // revoked (middleware failing open, a preview build, a signed-out session
  // whose cookie was never cleared) and there is nothing to recover. Offering
  // another reload would land in exactly the same place; "sign in" is the
  // only honest way out.
  const cookieGraceExpired = sessionMayStillBeValid && isStalled;

  return {
    isResolving: unresolved && !isStalled,
    isStalled: unresolved && isStalled && !cookieGraceExpired,
    isSignedOut: (!unresolved && !userDoc?.uid) || cookieGraceExpired,
    isReady: !!userDoc?.uid,
    retry: () => {
      setIsStalled(false);
      setAttempt((a) => a + 1);
      // refreshUserDoc returns false immediately without a uid, so when it is
      // Firebase Auth itself that never resolved, a reload is the only thing
      // that can actually clear the state we are offering this button for.
      if (user?.uid) void refreshUserDoc();
      else if (typeof window !== 'undefined') window.location.reload();
    },
  };
}
